/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../common-components/PageHeader';
import { useAppDispatch, useAppSelector } from '../../helpers/hooks';
import { useWorkspaceSlice } from './slice';
import {
  selectActiveJob,
  selectJobLocked,
  selectLoadingProject,
  selectProject,
  selectSelectedLanguage,
} from './slice/selectors';
import { selectLanguageMap } from '../languages/slice/selectors';
import { ROUTE_PATHS } from '../../routes/constants';
import TranslationsTab from './components/TranslationsTab';
import SettingsTab from './components/SettingsTab';
import DeliveryTab from './components/DeliveryTab';

export default function ProjectWorkspace() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projectId = '' } = useParams();
  const { actions } = useWorkspaceSlice();

  const project = useAppSelector(selectProject);
  const loading = useAppSelector(selectLoadingProject);
  const selectedLanguage = useAppSelector(selectSelectedLanguage);
  const languageMap = useAppSelector(selectLanguageMap);
  const activeJob = useAppSelector(selectActiveJob);
  const locked = useAppSelector(selectJobLocked);

  const [tab, setTab] = useState(0);

  useEffect(() => {
    dispatch(actions.loadWorkspaceRequest(projectId));
    return () => {
      dispatch(actions.resetWorkspace());
    };
  }, [projectId]);

  const handleLanguageChange = (code: string) => {
    dispatch(actions.setSelectedLanguage(code));
    dispatch(actions.fetchTranslationsRequest({ projectId, language: code }));
  };

  if (loading || !project) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(ROUTE_PATHS.PROJECTS)}
        sx={{ mb: 1 }}
      >
        Back to projects
      </Button>

      <PageHeader
        title={project.name}
        subtitle={`Source language: ${
          languageMap[project.sourceLanguage] || project.sourceLanguage
        }`}
        action={
          project.targetLanguages.length > 0 &&
          selectedLanguage && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Translating to:</Typography>
              <Select
                size="small"
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                sx={{ minWidth: 180 }}
              >
                {project.targetLanguages.map((code) => (
                  <MenuItem key={code} value={code}>
                    {languageMap[code] || code} ({code})
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          )
        }
      />

      {locked && activeJob && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <AlertTitle>Bulk translation in progress</AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Translating untranslated keys
            {activeJob.languages.length
              ? ` for ${activeJob.languages.join(', ')}`
              : ''}
            . Editing is locked until this finishes
            {activeJob.total > 0
              ? ` — ${activeJob.processed}/${activeJob.total} processed`
              : ''}
            .
          </Typography>
          <LinearProgress
            variant={
              activeJob.status === 'RUNNING' && activeJob.total > 0
                ? 'determinate'
                : 'indeterminate'
            }
            value={
              activeJob.total > 0
                ? Math.round((activeJob.processed / activeJob.total) * 100)
                : 0
            }
          />
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_e, value) => setTab(value)}
        sx={{ mb: 3, borderBottom: '1px solid #e0e0e0' }}
      >
        <Tab label="Translations" />
        <Tab label="Settings" />
        <Tab label="API & Export" />
      </Tabs>

      {tab === 0 && <TranslationsTab projectId={projectId} />}
      {tab === 1 && <SettingsTab projectId={projectId} />}
      {tab === 2 && <DeliveryTab projectId={projectId} />}
    </Box>
  );
}
